import {
  astToFilterExpression,
  type AttributeSelectionMap,
  type FilterAstNode,
  type ProductAttribute,
} from '@truss-security/truss-sdk-internal';
import type { OnboardingStepId } from './onboardingSteps';

export type OnboardingSelections = Partial<Record<OnboardingStepId, string[]>>;

const STEP_TO_ATTRIBUTE: [OnboardingStepId, ProductAttribute][] = [
  ['category', 'category'],
  ['industry', 'industry'],
  ['region', 'region'],
  ['source', 'source'],
  ['author', 'author'],
  ['tags', 'tags'],
];

function comparisonClause(attribute: ProductAttribute, value: string): FilterAstNode {
  return { type: 'comparison', attribute, operator: '=', value };
}

/** Flatten a list of comparison nodes into a left-associative OR tree. */
function combineWithOr(nodes: FilterAstNode[]): FilterAstNode | null {
  if (nodes.length === 0) return null;
  let acc = nodes[0]!;
  for (let i = 1; i < nodes.length; i++) {
    acc = { type: 'logical', operator: 'OR', left: acc, right: nodes[i]! };
  }
  return acc;
}

/**
 * Build filter AST for onboarding: every selected value is OR-ed together,
 * including values across different attributes (products rarely match all picks).
 */
export function onboardingSelectionsToOrAst(
  selections: OnboardingSelections
): FilterAstNode | null {
  const clauses: FilterAstNode[] = [];
  for (const [stepId, attribute] of STEP_TO_ATTRIBUTE) {
    const values = selections[stepId];
    if (!values?.length) continue;
    for (const value of values) {
      clauses.push(comparisonClause(attribute, value));
    }
  }
  return combineWithOr(clauses);
}

export function selectionsToAttributeMap(
  selections: OnboardingSelections
): AttributeSelectionMap {
  const map: AttributeSelectionMap = {};
  for (const [stepId, attr] of STEP_TO_ATTRIBUTE) {
    const values = selections[stepId];
    if (values && values.length > 0) {
      map[attr] = values;
    }
  }
  return map;
}

export function hasAnyOnboardingSelection(selections: OnboardingSelections): boolean {
  return Object.values(selections).some((v) => v && v.length > 0);
}

export function buildFilterExpressionFromSelections(
  selections: OnboardingSelections
): string | null {
  const ast = onboardingSelectionsToOrAst(selections);
  return ast ? astToFilterExpression(ast) : null;
}
